import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  TerminalWindow,
  Trash,
  DownloadSimple,
  ArrowsClockwise,
  MagnifyingGlass,
  CheckCircle,
  WarningCircle,
  XCircle,
  Info,
  Pause,
  Play,
  CaretDown,
  CaretRight
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API_BASE } from "../../lib/api";

const LEVEL_STYLES = {
  success: {
    badge: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    text: "text-emerald-300",
    icon: CheckCircle,
  },
  warning: {
    badge: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    text: "text-amber-300",
    icon: WarningCircle,
  },
  error: {
    badge: "bg-rose-500/15 text-rose-400 border-rose-500/30",
    text: "text-rose-300",
    icon: XCircle,
  },
  info: {
    badge: "bg-sky-500/15 text-sky-400 border-sky-500/30",
    text: "text-slate-300",
    icon: Info,
  },
};

const STEP_COLORS = {
  gmail: "text-red-400 bg-red-950/40 border-red-800/40",
  gemini: "text-purple-400 bg-purple-950/40 border-purple-800/40",
  drive: "text-amber-400 bg-amber-950/40 border-amber-800/40",
  sheets: "text-emerald-400 bg-emerald-950/40 border-emerald-800/40",
  pipeline: "text-blue-400 bg-blue-950/40 border-blue-800/40",
};

export function LogView() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveConnected, setLiveConnected] = useState(false);
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [stepFilter, setStepFilter] = useState("all");
  const [autoScroll, setAutoScroll] = useState(true);
  const [expandedIds, setExpandedIds] = useState({});

  const consoleEndRef = useRef(null);
  const eventSourceRef = useRef(null);

  // Carga inicial de logs existentes
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const data = await api.logs(300);
      setLogs(Array.isArray(data) ? data.reverse() : []);
    } catch (err) {
      console.warn("Error al cargar historial de logs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();

    // Conexión SSE en tiempo real
    const sseUrl = `${API_BASE}/logs/stream`;
    try {
      const es = new EventSource(sseUrl);
      eventSourceRef.current = es;

      es.onopen = () => {
        setLiveConnected(true);
      };

      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data && data.type !== "connected") {
            setLogs((prev) => {
              // Evitar duplicados por id
              if (prev.some((l) => l.id === data.id)) return prev;
              return [...prev, data];
            });
          }
        } catch (err) {
          // Ignorar mensajes no JSON (heartbeats)
        }
      };

      es.onerror = () => {
        setLiveConnected(false);
      };

      return () => {
        es.close();
      };
    } catch (e) {
      console.warn("SSE no disponible, usando polling:", e);
    }
  }, []);

  // Auto-scroll al fondo cuando llegan nuevos logs
  useEffect(() => {
    if (autoScroll && consoleEndRef.current) {
      consoleEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, autoScroll]);

  // Limpiar logs
  const handleClear = async () => {
    if (!window.confirm("¿Seguro que deseas vaciar el registro de logs?")) return;
    try {
      await api.clearLogs();
      setLogs([]);
      toast.success("Registro de logs vaciado.");
    } catch (err) {
      toast.error("No se pudieron borrar los logs.");
    }
  };

  // Exportar logs a archivo de texto / JSON
  const handleExport = () => {
    if (!logs.length) {
      toast.info("No hay logs para exportar.");
      return;
    }
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `facturapp_logs_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Archivo de logs descargado.");
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Filtrado de logs
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (levelFilter !== "all" && log.level !== levelFilter) return false;
      if (stepFilter !== "all" && log.step !== stepFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const msg = (log.message || "").toLowerCase();
        const step = (log.step || "").toLowerCase();
        const details = JSON.stringify(log.details || {}).toLowerCase();
        return msg.includes(q) || step.includes(q) || details.includes(q);
      }
      return true;
    });
  }, [logs, levelFilter, stepFilter, search]);

  const counts = useMemo(() => {
    return {
      all: logs.length,
      success: logs.filter((l) => l.level === "success").length,
      warning: logs.filter((l) => l.level === "warning").length,
      error: logs.filter((l) => l.level === "error").length,
      info: logs.filter((l) => l.level === "info").length,
    };
  }, [logs]);

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Barra de Herramientas y Filtros */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Izquierda: Estado en vivo y Buscador */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900 text-xs font-mono border border-slate-800">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                liveConnected ? "bg-emerald-400 animate-pulse" : "bg-amber-400"
              }`}
            />
            <span className="text-slate-300 font-medium">
              {liveConnected ? "Stream en vivo activo" : "Conectando stream..."}
            </span>
          </div>

          <div className="relative min-w-[240px]">
            <MagnifyingGlass
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Buscar en logs (proveedor, CIF, paso)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
            />
          </div>
        </div>

        {/* Derecha: Botones de Acción */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              autoScroll
                ? "bg-slate-100 text-slate-700 border-slate-300"
                : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"
            }`}
            title={autoScroll ? "Pausar auto-scroll" : "Activar auto-scroll"}
          >
            {autoScroll ? <Pause size={14} /> : <Play size={14} />}
            Auto-scroll
          </button>

          <button
            onClick={fetchLogs}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            title="Recargar logs"
          >
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
            Refrescar
          </button>

          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            title="Exportar JSON"
          >
            <DownloadSimple size={14} />
            Exportar
          </button>

          <button
            onClick={handleClear}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-lg hover:bg-rose-100 transition-colors"
            title="Limpiar registro"
          >
            <Trash size={14} />
            Limpiar
          </button>
        </div>
      </div>

      {/* Filtros por Nivel y Módulo */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-1.5 bg-slate-200/60 p-1 rounded-lg">
          {[
            { id: "all", label: `Todos (${counts.all})` },
            { id: "success", label: `Éxitos (${counts.success})`, color: "text-emerald-700" },
            { id: "info", label: `Info (${counts.info})`, color: "text-sky-700" },
            { id: "warning", label: `Avisos (${counts.warning})`, color: "text-amber-700" },
            { id: "error", label: `Errores (${counts.error})`, color: "text-rose-700" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setLevelFilter(tab.id)}
              className={`px-3 py-1 rounded-md font-medium transition-all ${
                levelFilter === tab.id
                  ? "bg-white text-slate-900 shadow-sm"
                  : `text-slate-600 hover:text-slate-900 ${tab.color || ""}`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <span className="text-slate-400 font-medium mr-1">Módulo:</span>
          {["all", "gmail", "gemini", "drive", "sheets", "pipeline"].map((s) => (
            <button
              key={s}
              onClick={() => setStepFilter(s)}
              className={`px-2 py-0.5 rounded text-[11px] font-mono uppercase transition-colors ${
                stepFilter === s
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Consola de Logs */}
      <div className="bg-slate-950 rounded-xl border border-slate-800 shadow-2xl overflow-hidden font-mono text-xs text-slate-300">
        {/* Cabecera de la terminal */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900/90 border-b border-slate-800/80">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
            </div>
            <span className="text-slate-400 font-medium text-[11px] ml-2 flex items-center gap-1.5">
              <TerminalWindow size={14} className="text-slate-500" />
              facturapp-pipeline.log · {filteredLogs.length} eventos mostrados
            </span>
          </div>
          <div className="text-[11px] text-slate-500 font-mono">
            {liveConnected ? "SSE CONNECTED" : "POLLING MODE"}
          </div>
        </div>

        {/* Lista de Registros */}
        <div className="p-4 overflow-y-auto max-h-[640px] space-y-1.5 select-text">
          {filteredLogs.length === 0 ? (
            <div className="py-16 text-center text-slate-500 space-y-2">
              <TerminalWindow size={32} className="mx-auto text-slate-600 stroke-[1.5]" />
              <div className="text-sm font-medium">No hay eventos registrados en este filtro.</div>
              <div className="text-xs text-slate-600">
                Pulsa en <strong>«Ejecutar polling ahora»</strong> para ver el flujo en vivo.
              </div>
            </div>
          ) : (
            filteredLogs.map((log) => {
              const levelCfg = LEVEL_STYLES[log.level] || LEVEL_STYLES.info;
              const Icon = levelCfg.icon;
              const stepBadge = STEP_COLORS[log.step] || "text-slate-400 bg-slate-800 border-slate-700";
              const timeStr = log.timestamp
                ? new Date(log.timestamp).toLocaleTimeString("es-ES", { hour12: false })
                : "--:--:--";
              const hasDetails = log.details && Object.keys(log.details).length > 0;
              const isExpanded = expandedIds[log.id];

              return (
                <div
                  key={log.id || `${log.timestamp}-${log.message}`}
                  className="group hover:bg-slate-900/60 rounded p-1.5 transition-colors border border-transparent hover:border-slate-800/60"
                >
                  <div className="flex items-start gap-2.5 leading-relaxed">
                    {/* Hora */}
                    <span className="text-slate-500 shrink-0 select-none text-[11px] pt-0.5">
                      {timeStr}
                    </span>

                    {/* Nivel */}
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.2 rounded border text-[10px] font-semibold uppercase tracking-wider shrink-0 ${levelCfg.badge}`}
                    >
                      <Icon size={11} weight="bold" />
                      {log.level}
                    </span>

                    {/* Paso */}
                    <span
                      className={`inline-block px-1.5 py-0.2 rounded border text-[10px] uppercase font-bold tracking-wider shrink-0 ${stepBadge}`}
                    >
                      {log.step}
                    </span>

                    {/* Mensaje */}
                    <div className={`flex-1 break-words ${levelCfg.text}`}>
                      {log.message}
                    </div>

                    {/* Botón de Detalles si hay JSON */}
                    {hasDetails && (
                      <button
                        onClick={() => toggleExpand(log.id)}
                        className="text-slate-500 hover:text-slate-300 flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-800/80 text-[10px] shrink-0"
                      >
                        {isExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                        JSON
                      </button>
                    )}
                  </div>

                  {/* Detalle Desplegable */}
                  {hasDetails && isExpanded && (
                    <div className="mt-2 ml-14 p-2.5 rounded bg-slate-900 border border-slate-800 text-[11px] text-slate-400 overflow-x-auto">
                      <pre>{JSON.stringify(log.details, null, 2)}</pre>
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div ref={consoleEndRef} />
        </div>
      </div>
    </div>
  );
}
